import React, { useEffect, useState } from 'react';
import { X, ArrowsLeftRight, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Transfer Show modal (MASTER / LMASTER only).
 *
 * Lets a platform admin reassign a show's ownership to another producer.
 * Used when an admin set up a show on behalf of a producer (common at
 * launch / onboarding) and now wants the producer to fully own it so
 * payment splits route to their connected Stripe account instead of the
 * platform.
 *
 * UX:
 *  - Search box filters platform users by name/email (admin-only API).
 *  - Only producers (ORG_ADMIN+) are selectable; the modal explains why
 *    plain riders aren't valid targets.
 *  - Confirmation step shows from/to and the impact ("payments will now
 *    route to {newOwner}'s connected Stripe account").
 */
export const TransferShowModal = ({ showId, showName, currentOwnerName, onClose, onTransferred }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Debounced search — runs every time `query` changes, 250 ms after the
  // last keystroke. Keeps the admin-users endpoint from getting hammered.
  useEffect(() => {
    const handle = setTimeout(async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get('/admin/users', { params: { q: query.trim(), limit: 20 } });
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'User search failed');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const isProducer = (u) => {
    const roles = u?.roles || (u?.role ? [u.role] : []);
    return roles.some((r) => ['ORG_ADMIN', 'MASTER', 'LMASTER'].includes(r));
  };

  const submit = async () => {
    if (!selected) return;
    if (!isProducer(selected)) {
      toast.error('Selected user is not a producer. Promote them under Platform Settings → Users first.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shows/${showId}/transfer`, {
        target_user_id: selected.id,
      });
      toast.success(`Show transferred to ${data.new_owner_name || data.new_owner_email}`);
      if (onTransferred) onTransferred(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" data-testid="transfer-show-modal">
      <div className="bg-[#111] border border-[#262626] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#1F1F1F]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5E5CE6]/15 flex items-center justify-center flex-shrink-0">
              <ArrowsLeftRight size={20} weight="duotone" className="text-[#5E5CE6]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Transfer Show</h2>
              <p className="text-xs text-[#9E9E9E] mt-0.5">
                Move <span className="text-white">"{showName}"</span> to another producer
              </p>
              {currentOwnerName && (
                <p className="text-[10px] text-[#6B6B6B] mt-0.5">Currently owned by: {currentOwnerName}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#9E9E9E] hover:text-white p-1"
            data-testid="transfer-show-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {/* Search */}
          <label className="block text-xs uppercase tracking-wider text-[#9E9E9E] font-semibold mb-2">
            Search Producer
          </label>
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#5E5CE6]"
              autoFocus
              data-testid="transfer-show-search-input"
            />
          </div>

          {/* Results */}
          {loading && <p className="text-xs text-[#9E9E9E]">Searching…</p>}
          {!loading && query.trim() && results.length === 0 && (
            <p className="text-xs text-[#9E9E9E] py-4 text-center">No users match "{query}".</p>
          )}
          {!loading && results.length > 0 && (
            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="transfer-show-results">
              {results.map((u) => {
                const sel = selected?.id === u.id;
                const isProd = isProducer(u);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => isProd && setSelected(u)}
                    disabled={!isProd}
                    className={`w-full text-left p-3 rounded-md border transition-colors flex items-center gap-3 ${
                      sel
                        ? 'bg-[#5E5CE6]/15 border-[#5E5CE6]/50'
                        : isProd
                          ? 'bg-[#0A0A0A] border-[#262626] hover:border-[#5E5CE6]/40'
                          : 'bg-[#0A0A0A] border-[#1A1A1A] opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`transfer-show-user-${u.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#262626] flex items-center justify-center flex-shrink-0 text-xs text-white font-bold">
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{u.name || u.email}</p>
                      <p className="text-[10px] text-[#9E9E9E] truncate">{u.email}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(u.roles || [u.role]).filter(Boolean).map((r) => (
                          <span
                            key={r}
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              r === 'MASTER' || r === 'LMASTER'
                                ? 'bg-[#FFB000]/20 text-[#FFB000]'
                                : r === 'ORG_ADMIN'
                                  ? 'bg-[#34C759]/20 text-[#34C759]'
                                  : 'bg-[#262626] text-[#9E9E9E]'
                            }`}
                          >
                            {r}
                          </span>
                        ))}
                        {u.organization_name && (
                          <span className="text-[9px] text-[#6B6B6B]">· {u.organization_name}</span>
                        )}
                      </div>
                      {!isProd && (
                        <p className="text-[9px] text-[#FF9500] mt-1">Not a producer — promote first under Users panel</p>
                      )}
                    </div>
                    {sel && <Check size={16} weight="bold" className="text-[#5E5CE6] flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Confirmation */}
          {selected && (
            <div className="mt-5 bg-[#5E5CE6]/10 border border-[#5E5CE6]/40 rounded-md p-3" data-testid="transfer-show-confirm">
              <p className="text-xs text-white">
                <span className="font-bold">"{showName}"</span> will be reassigned to{' '}
                <span className="font-bold">{selected.name || selected.email}</span>.
              </p>
              <p className="text-[10px] text-[#9E9E9E] mt-1.5 leading-relaxed">
                Going forward, payments from this show will route to the new owner's connected Stripe account
                (minus the platform service fee). Existing entries, scores, payouts, and reservations are
                preserved.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#1F1F1F]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-[#262626] hover:bg-[#333] text-white text-sm font-bold rounded-md"
            data-testid="transfer-show-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!selected || submitting}
            className="px-4 py-2 bg-[#5E5CE6] hover:bg-[#5E5CE6]/90 disabled:opacity-50 text-white text-sm font-bold rounded-md flex items-center gap-1.5"
            data-testid="transfer-show-submit"
          >
            <ArrowsLeftRight size={14} weight="bold" />
            {submitting ? 'Transferring…' : 'Transfer Show'}
          </button>
        </div>
      </div>
    </div>
  );
};
